/**
 * Minimal cron helpers for scheduled configuration forms.
 *
 * The proxy scheduler accepts a 5-field cron expression. The UI offers a
 * friendly picker (presets + simple every-N-min/hour) but always stores
 * the underlying cron string so users can switch to "advanced" and edit
 * it directly.
 */

export interface CronDescription {
  /** Concise label used in cards and chips (e.g. "Every 15 min", "Hourly", "Custom"). */
  short: string
  /** Longer human-readable description for forms and tooltips. */
  long: string
}

export const CRON_PRESETS: Array<{ label: string; expression: string; description: string }> = [
  { label: 'Every 5 minutes', expression: '*/5 * * * *', description: 'Every 5 minutes' },
  { label: 'Every 15 minutes', expression: '*/15 * * * *', description: 'Every 15 minutes' },
  { label: 'Every 30 minutes', expression: '*/30 * * * *', description: 'Every 30 minutes' },
  {
    label: 'Hourly (top of hour)',
    expression: '0 * * * *',
    description: 'Every hour, on the hour'
  },
  { label: 'Every 6 hours', expression: '0 */6 * * *', description: 'Every 6 hours' },
  { label: 'Daily at midnight', expression: '0 0 * * *', description: 'Once a day at 00:00' }
]

const EVERY_MINUTE_RE = /^\*\s+\*\s+\*\s+\*\s+\*$/
const EVERY_N_MIN_RE = /^\*\/(\d+)\s+\*\s+\*\s+\*\s+\*$/
const EVERY_N_HOUR_RE = /^0\s+\*\/(\d+)\s+\*\s+\*\s+\*$/
const HOURLY_RE = /^0\s+\*\s+\*\s+\*\s+\*$/
const DAILY_RE = /^0\s+0\s+\*\s+\*\s+\*$/

/**
 * Translate a cron expression into a friendly label. Falls back to
 * `"Custom: <expr>"` for anything we don't have a canned description for,
 * and to `"Manual only"` for the empty / undefined / paused case.
 */
export function describeCron(expression: string | undefined | null): CronDescription {
  const trimmed = (expression ?? '').trim()
  if (!trimmed) return { short: 'Manual only', long: 'No schedule — runs only on manual dispatch.' }
  for (const preset of CRON_PRESETS) {
    if (preset.expression === trimmed) {
      return { short: preset.label, long: preset.description }
    }
  }
  if (EVERY_MINUTE_RE.test(trimmed)) {
    return { short: 'Every minute', long: 'Every minute' }
  }
  if (HOURLY_RE.test(trimmed)) return { short: 'Hourly', long: 'Every hour, on the hour' }
  if (DAILY_RE.test(trimmed)) return { short: 'Daily', long: 'Once a day at 00:00' }
  const minMatch = trimmed.match(EVERY_N_MIN_RE)
  if (minMatch) {
    const n = Number(minMatch[1])
    return { short: `Every ${n} min`, long: `Every ${n} minutes` }
  }
  const hourMatch = trimmed.match(EVERY_N_HOUR_RE)
  if (hourMatch) {
    const n = Number(hourMatch[1])
    return { short: `Every ${n} h`, long: `Every ${n} hours` }
  }
  return { short: 'Custom', long: `Custom cron: ${trimmed}` }
}

/**
 * Best-effort syntactic check of a 5-field cron expression. Permissive on
 * the field contents (we only care that the shape is right and the proxy
 * will surface a real error if it isn't); returns `null` for valid input
 * and a short error message otherwise.
 */
export function validateCron(expression: string): string | null {
  const trimmed = expression.trim()
  if (!trimmed) return null
  const fields = trimmed.split(/\s+/)
  if (fields.length !== 5) {
    return 'Cron expression must have exactly 5 fields (minute hour day-of-month month day-of-week).'
  }
  const fieldRe = /^[\d*/,\-?LW#]+$/
  for (let i = 0; i < fields.length; i++) {
    if (!fieldRe.test(fields[i])) {
      return `Field ${i + 1} contains unexpected characters.`
    }
  }
  return null
}
